import React, { FC, useEffect, useRef, useState } from "react";
import SearchBar from "./SearchBar";
import MobileSearch from "./MobileSearch";
import { useData } from "data/data-provider";
import { Checkbox } from "@headlessui/react";
import { CheckIcon } from "@heroicons/react/24/solid";
import RenterInfo from "./RenterInfo";
import DriverInfo from "./DriverInfo";
import { globalJson } from "global/global_json";
import { getBonzahProducts } from "api/bonzah/bonzah";
import {
  convertTimeToTimezone,
  convertToDateObjectWithTimezone,
  formatTimestamp,
} from "utils/date-format";
import Badge from "shared/Badge/Badge";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { tripModificationRequest } from "api/booking/booking";
import toast from "react-hot-toast";
import VehicleSwap from "./VehicleSwap";
import { getCustomer } from "api/customer/customer";

interface EditBookingProps {
  bookingDetails: any;
}

const EditBooking: FC<EditBookingProps> = ({ bookingDetails }) => {
  const {
    bonzahProducts,
    setBonzahProducts,
    selectedBonzahProducts,
    selectedCar,
    setDropoffLocationType,
    setPickupLocation,
    setDropoffLocation,
    setStartDate,
    setEndDate,
    setSelectedCar,
    selectedExtras,
    setSelectedExtras,
    pickupLocation,
    startHours,
    startMinutes,
    endHours,
    endMinutes,
    setEndPeriod,
    setStartPeriod,
    setSelectedBonzahProducts,
    setSearchedPickupLocation,
    setSearchedDropoffLocation,
    setStartHours,
    setStartMinutes,
    setEndHours,
    setEndMinutes,
  } = useData();
  const [selected, setSelected] = useState("Trip Info");
  const options = ["Trip Info", "Vehicle Swap", "Renter Info", "Add Driver"];
  const hasFetched = useRef(false);
  const [startTime, setStartTime] = useState(`${startHours}:${startMinutes}`); // Default start time
  const [endTime, setEndTime] = useState(`${endHours}:${endMinutes}`);

  const handleBonzahProductSelection = (productType: any) => {
    setSelectedBonzahProducts(productType);
  };

  console.log(bookingDetails);
  useEffect(() => {
    if (!bookingDetails || hasFetched.current) return; // Prevent re-fetching on tab switch
    hasFetched.current = true;
    if (bookingDetails) {
      setStartHours(formatTimestamp(bookingDetails.pick_up_time).hours);
      setStartMinutes(formatTimestamp(bookingDetails.pick_up_time).minutes);

      setEndHours(formatTimestamp(bookingDetails.drop_off_time).hours);
      setEndMinutes(formatTimestamp(bookingDetails.drop_off_time).minutes);
    }

    setDropoffLocationType(bookingDetails?.same_drop_off);
    setPickupLocation(bookingDetails?.pick_up_location);
    setSearchedPickupLocation(bookingDetails?.custom_pick_up_address);
    setSearchedDropoffLocation(bookingDetails?.custom_drop_off_address);
    setDropoffLocation(bookingDetails?.drop_off_location);
    setStartTime(
      convertTimeToTimezone(
        bookingDetails?.pick_up_time,
        bookingDetails?.timezone
      ).time
    );
    setStartPeriod(
      convertTimeToTimezone(
        bookingDetails?.pick_up_time,
        bookingDetails?.timezone
      ).period
    );
    setEndTime(
      convertTimeToTimezone(
        bookingDetails?.drop_off_time,
        bookingDetails?.timezone
      ).time
    );
    setEndPeriod(
      convertTimeToTimezone(
        bookingDetails?.drop_off_time,
        bookingDetails?.timezone
      ).period
    );
    setStartDate(
      convertToDateObjectWithTimezone(
        bookingDetails?.pick_up_time,
        bookingDetails?.timezone
      )
    );
    setEndDate(
      convertToDateObjectWithTimezone(
        bookingDetails?.drop_off_time,
        bookingDetails?.timezone
      )
    );
    setSelectedCar(bookingDetails?.fleet);

    if (bookingDetails?.extras?.length > 0) {
      const extraIds = new Set<number>(
        bookingDetails.extras.map((extra: any) => extra.id)
      );
      setSelectedExtras(extraIds);
    }

    if (bookingDetails?.bonzah_insurance_options?.length > 0) {
      const insuranceIds = bookingDetails?.bonzah_insurance_options?.map(
        (insurance: any) => insurance
      );
      setSelectedBonzahProducts(insuranceIds);
    }

    fetchBonzahProducts();
  }, []);

  const fetchBonzahProducts = async (selectedType?: string) => {
    const payload = {
      company_id:
        process.env.REACT_APP_ENV !== "prod"
          ? globalJson.dev_company_id
          : globalJson.prod_company_id,
      selected_type: selectedType,
    };

    const { data, error } = await getBonzahProducts(payload);

    if (data) {
      setBonzahProducts(data);
    } else if (error) {
    }
  };

  useEffect(() => {
    const fetchCustomer = async () => {
      try {
        const response = await getCustomer(bookingDetails?.customer.email);
        if (response) {
          localStorage.setItem("access_token", response.access_token);
          localStorage.setItem("refresh_token", response.refresh_token);
          localStorage.setItem("user", JSON.stringify(response));
        }
      } catch (err) {
        toast.error("An error occurred while processing your request.");
      }
    };
    fetchCustomer();
  }, []);

  const renderDescriptionWithLink = (description: string) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = description.split(urlRegex);
    return parts.map((part, index) => {
      if (urlRegex.test(part)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 hover:underline"
          >
            {part}
          </a>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  const handleExtraChange = (extraId: any, checked: any) => {
    setSelectedExtras((prevSelectedExtras) => {
      const updatedExtras = new Set(prevSelectedExtras);
      if (checked) {
        updatedExtras.add(extraId);
      } else {
        updatedExtras.delete(extraId);
      }
      return updatedExtras;
    });
  };

  const renderBonzahSection = () => {
    return (
      <div className="w-full flex flex-col sm:rounded-2xl border-b sm:border-t sm:border-l sm:border-r border-neutral-200 dark:border-neutral-700 space-y-6 mt-4 border-none">
        <h2 className="text-2xl text-[#111827] dark:text-white font-bold">
          Trip Insurance
        </h2>
        {bonzahProducts?.insurance_options
          ?.filter((product: any) => !(product.insurance_by_bonzah === true))
          .map((product: any) => (
            <div
              key={product.title}
              className="flex flex-col justify-between items-start border rounded-lg p-4 bg-white dark:bg-transparent max-w-full"
            >
              <div className="flex items-start">
                <Checkbox
                  checked={product?.value?.some((val: any) =>
                    selectedBonzahProducts?.includes(val)
                  )}
                  onChange={() => handleBonzahProductSelection(product?.value)}
                  className="group size-7 rounded-md p-1 ring-1 ring-primary-200 ring-inset data-[checked]:bg-primary-200 data-[checked]:bg-opacity-20  mr-4"
                >
                  <CheckIcon
                    className={`size-5 stroke-primary-200 ${
                      product?.value?.some((val: any) =>
                        selectedBonzahProducts?.includes(val)
                      )
                        ? "visible"
                        : "invisible"
                    }`}
                  />
                </Checkbox>

                <div>
                  <div className="flex items-center mb-2">
                    <h2 className="text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100">
                      {product.title}
                    </h2>
                    {product?.pdf !== "" && (
                      <button
                        className="ml-2 text-gray-500 dark:text-gray-400"
                        onClick={() => window.open(product.pdf, "_blank")}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          fill="none"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fill="#1B2430"
                            d="M9.997 18.958c-4.941 0-8.958-4.016-8.958-8.958s4.017-8.958 8.958-8.958c4.942 0 8.959 4.016 8.959 8.958s-4.017 8.958-8.959 8.958Zm0-16.666C5.747 2.292 2.29 5.75 2.29 10s3.458 7.708 7.708 7.708S17.706 14.25 17.706 10s-3.459-7.708-7.709-7.708Z"
                          />
                          <path
                            fill="#1B2430"
                            d="M10 11.458a.63.63 0 0 1-.625-.625V6.667A.63.63 0 0 1 10 6.042a.63.63 0 0 1 .625.625v4.166a.63.63 0 0 1-.625.625Zm-.003 2.709a.829.829 0 0 1-.316-.067.963.963 0 0 1-.275-.175.86.86 0 0 1-.175-.275.83.83 0 0 1-.067-.317.83.83 0 0 1 .067-.316c.041-.1.1-.192.175-.275a.96.96 0 0 1 .275-.175.833.833 0 0 1 .633 0c.1.041.192.1.275.175a.963.963 0 0 1 .175.275.83.83 0 0 1 .067.316.83.83 0 0 1-.067.317.86.86 0 0 1-.175.275.964.964 0 0 1-.275.175.83.83 0 0 1-.317.067Z"
                          />
                        </svg>
                      </button>
                    )}
                  </div>

                  {product?.options &&
                    Object.entries(product.options).map(([key, values]) => (
                      <div key={key} className="mt-2">
                        {!product.canopy && product.insurance_by_bonzah && (
                          <h3 className="font-semibold text-gray-700 dark:text-gray-300 uppercase">
                            {key}
                          </h3>
                        )}

                        <p
                          className={`lg:text-lg text-md text-gray-600 dark:text-gray-400 `}
                        >
                          {(Array.isArray(values) ? values : [values]).map(
                            (detail, index) => (
                              <span
                                className={`${
                                  product.canopy
                                    ? "text-red-500 font-semibold"
                                    : "text-gray-600"
                                } `}
                                key={index}
                              >
                                {detail}
                                <br />
                              </span>
                            )
                          )}
                        </p>
                      </div>
                    ))}
                </div>
              </div>

              <div className="mt-4 md:mt-0 w-full flex justify-end items-center">
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  ${product.price.toFixed(2)}
                  <span className="text-gray-400 lg:text-lg">
                    /
                    {product.nature === "per_trip"
                      ? "trip"
                      : product.nature === "per_day"
                      ? "day"
                      : "24 hours"}
                  </span>
                </p>
              </div>
            </div>
          ))}
      </div>
    );
  };

  const renderExtrasSection = () => {
    return (
      <div className="w-full flex flex-col sm:rounded-2xl border-b sm:border-t sm:border-l sm:border-r border-neutral-200 dark:border-neutral-700 space-y-6  border-none mt-4">
        <h2 className="text-2xl font-bold text-[#111827] dark:text-white">
          Extras
        </h2>

        {selectedCar?.extras.map((extra) => (
          <div
            key={extra.id}
            className="w-full rounded-3xl border border-gray-200 flex justify-between items-center p-5"
          >
            <div className="flex justify-start">
              <Checkbox
                checked={selectedExtras?.has(extra.id)}
                onChange={(checked) => handleExtraChange(extra.id, checked)}
                className="group size-7 rounded-md  p-1 ring-1 ring-primary-200 ring-inset data-[checked]:bg-primary-200 data-[checked]:bg-opacity-20  mr-4"
              >
                <CheckIcon
                  className={`size-5 stroke-primary-200 ${
                    selectedExtras.has(extra.id) ? "visible" : "invisible"
                  }`}
                />
              </Checkbox>
              <div>
                <div className="text-gray-900 dark:text-gray-200 lg:text-lg sm:text-l font-bold flex space-x-2">
                  <span>{extra.name}</span>
                  {extra.is_required === true && (
                    <Badge color="blue" name="Recommended" />
                  )}
                </div>
                <div className="text-gray-400 dark:text-neutral-400 lg:text-lg sm:text-md font-medium ">
                  {renderDescriptionWithLink(extra.description)}
                </div>
              </div>
            </div>

            <div className="mt-4 md:mt-0">
              <p className="lg:text-2xl text-lg font-bold text-gray-900 dark:text-gray-100 flex justify-end items-center">
                ${extra.amount}
                <span className="text-gray-400 lg:text-lg">
                  {" "}
                  /{extra.nature === "per_day" ? "day" : "trip"}
                </span>
              </p>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const TripModificationRequest = async () => {
    const payload = {
      booking: bookingDetails?.id,
      extras: Array.from(selectedExtras),
      bonzah_insurance_options: selectedBonzahProducts,
    };
    try {
      const result = await tripModificationRequest(payload);
      if (!result.error) {
        toast.success("Your booking modification request has been sent.");
      } else {
        toast.error(result.error);
      }
    } catch (err) {
      toast.error("An error occurred while processing your request.");
    }
  };

  return (
    <div className="border border-dotted my-4 p-3 md:p-6">
      <div className="flex items-center justify-between bg-gray-50 rounded-md shadow-sm">
        {options.map((option) => (
          <button
            key={option}
            onClick={() => setSelected(option)} // Update the selected state
            className={`px-1 md:px-6 py-2 text-xs md:text-sm font-semibold rounded-md ${
              selected === option
                ? "bg-primary-200 text-white"
                : "bg-transparent text-[#263238] hover:bg-gray-200"
            }`}
          >
            {option}
          </button>
        ))}
      </div>
      {selected === "Trip Info" && (
        <>
          <div className="hidden lg:flex">
            <SearchBar
              startTime={startTime}
              setStartTime={setStartTime}
              endTime={endTime}
              setEndTime={setEndTime}
              bookingDetails={bookingDetails}
            />
          </div>
          <div className="flex lg:hidden  mt-4">
            <MobileSearch
              startTime={startTime}
              setStartTime={setStartTime}
              endTime={endTime}
              setEndTime={setEndTime}
              bookingDetails={bookingDetails}
            />
          </div>
          <div>
            {bookingDetails?.bonzah_insurance_options !== null &&
              renderBonzahSection()}
            {renderExtrasSection()}
            <ButtonPrimary
              className="mt-6 w-full"
              onClick={TripModificationRequest}
            >
              Update
            </ButtonPrimary>
          </div>
        </>
      )}
      {selected === "Vehicle Swap" && (
        <>
          <VehicleSwap bookingDetails={bookingDetails} />
        </>
      )}
      {selected === "Renter Info" && (
        <>
          <RenterInfo bookingDetails={bookingDetails} />
        </>
      )}
      {selected === "Add Driver" && (
        <>
          <DriverInfo bookingDetails={bookingDetails} />
        </>
      )}
    </div>
  );
};

export default EditBooking;
